import { Injectable } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { CommonHttpService } from '../../shared/services/http.service';
import { Response } from '@angular/http';
import { PaymentStepsService } from './payment-steps.service';
import { PaymentMethodModel } from '../../shared/models/payment-method.model';
import { AddressModel } from '../../shared/models/address.model';
import { DeliveryStepsService } from './delivery-steps.service';
import { PaymentDataModel } from '../../shared/models/payment/data.model';
import { TranslateService } from '@ngx-translate/core';
import { CardModel } from '../../shared/models/payment/card.model';
import { ScheduleService } from './schedule.service';

@Injectable()
export class PaymentService {
    private payingSource = new Subject();
    private paidSource = new Subject();
    private refusedSource = new Subject();
    private contractsAcceptedSource = new Subject();
    private contractsArePendingSource = new Subject();

    protected data = new PaymentDataModel;
    protected deliverable = false;
    protected offers;
    protected reCaptchaToken: string = null;
    protected requiredAcceptanceContracts: Array<any> = [];
    protected signedContracts: Array<any> = [];

    public paying$ = this.payingSource.asObservable();
    public paid$ = this.paidSource.asObservable();
    public refused$ = this.refusedSource.asObservable();
    public contractsAccepted$ = this.contractsAcceptedSource.asObservable();
    public contractsPending$ = this.contractsArePendingSource.asObservable();

    constructor(
        protected translate: TranslateService,
        protected http: CommonHttpService,
        protected paymentSteps: PaymentStepsService,
        protected deliveryStepsService: DeliveryStepsService,
        protected scheduleService: ScheduleService
    ) {
        this.paymentSteps.changedPaymentMethod$.subscribe((paymentMethod: PaymentMethodModel) => {
            this.data.card = new CardModel();
            this.data.cards = [];
            if (paymentMethod !== null) {
              this.data.payment_method = paymentMethod.id;
            }
        });
        this.deliveryStepsService.selectedAddress$.subscribe((address: AddressModel) => {
            this.data.deliveries.delivery_address = address;
        });
        this.scheduleService.selectedScheduleDate$.subscribe(date => {
            this.data.scheduling.date = date;
        });
        this.scheduleService.selectedSchedulePeriod$.subscribe(period => {
            this.data.scheduling.period = period.id;
        });
    }

    /**
     * Calling payment api method.
     **/
    public pay() {
        this.payingSource.next(null);

        this.http
            .useAuth()
            .mustBeBlessed()
            .post('checkout/payment', this.formatData())
            .subscribe((response: Response) => {
                const data = response.json().data;

                if (data.error === undefined) {
                    this.offers = data;
                    this.paidSource.next(this.offers);
                    return;
                }

                this.handleRefused(data.error);
                return;
            }, (response: Response) => {
                return this.translate.get([
                    'PAYMENT.PAYMENT.PAYMENT_REJECTED_MESSAGE',
                    'PAYMENT.PAYMENT.PAYMENT_INVALID_DATA',
                ]).subscribe((translated: string[]) => {
                    let errStr = translated['PAYMENT.PAYMENT.PAYMENT_REJECTED_MESSAGE'];
                    if (response.status === 422) {
                        const errArr = [];
                        const errors = response.json().errors;
                        errStr = `<p>${translated['PAYMENT.PAYMENT.PAYMENT_INVALID_DATA']}:</p>`;

                        for (let field in errors) {
                            for (let error of errors[field]) {
                                errArr.push(`<li>${error}</li>`);
                            }
                        }
                        errStr += `<ul>${errArr.join('')}</ul>`;
                    }

                    this.handleRefused(errStr);
                });
            });
    }


    public forceRefused(reason: string) {
        // Simula o fluxo real completo
        this.payingSource.next(null);
        setTimeout(() => {
            this.refusedSource.next(reason);
        }, 1000);
    }

    /**
     * Format data to send to payment registering API.
     **/
    public formatData() {
        if (!this.deliverable) {
            delete this.data.deliveries.delivery_address;
        }
        this.buildBillingAddress();
        this.buildCard();
        this.buildSchedule();
        this.buildReCaptchaToken();
        this.buildAffiliateShareableKey();
        this.buildContracts();
        this.build3ds();
        this.buildPagarMe3ds();
        this.buildFingerprint();
        this.buildBrowser();

        return this.data;
    }

    /**
     * Payment data installments setter.
     **/
    public setInstallments(installments: number) {
        this.data.installments = installments;
    }

    /**
     * 3ds object setter.
     */
    public set3ds(secureData: object) {
        this.data.three_ds = secureData;
    }

    /**
     * PagarMe 3DS result setter.
     */
    public setPagarMe3ds(data: object) {
        this.data.pagar_me_3ds = data;
    }

    /**
     * Fingerprint setter.
     */
    public setFingerprint(fingerprint: string) {
        this.data.fingerprint = fingerprint;
    }

    /**
     * Browser object setter.
     */
    public setBrowser(browser: object) {
        this.data.browser = browser;
    }

    /**
     * Payment data billing address setter.
     **/
    public setBillingAddress(address: AddressModel) {
        this.data.deliveries.billing_address = address;
    }

    /**
     * Payment data card setter.
     **/
    public setCard(card: CardModel) {
        this.data.cards = [];
        this.data.card = card;
    }
    /**
     * Multiple payment data card setter.
     **/
    public pushCard(index, card: CardModel) {
        this.data.card = new CardModel;

        if (this.data.cards === undefined) {
            this.data.cards = [];
        }
        this.data.cards[index - 1] = card;
    }
    /** Cards array getter */
    public getCards() {
        return this.data.cards;
    }

    /**
     * Deliverable setter.
     **/
    public setDeliverable(deliverable: boolean) {
        this.deliverable = deliverable;

        return this;
    }

    /**
     * reCaptchaToken setter.
     **/
    public setReCaptchaToken(reCaptchaToken: string) {
        this.reCaptchaToken = reCaptchaToken;

        return this;
    }

    /**
     * Check if offers array is setted and not empty.
     **/
    public hasOffers() {
        return this.offers !== undefined && this.offers.length > 0;
    }

    /**
     * Offers attribute getter.
     **/
    public getOffers() {
        return this.offers;
    }

    /**
     * Requires acceptance contracts setter
     **/
    public setRequiresAcceptanceContracts(contracts) {
        this.requiredAcceptanceContracts = contracts;
        return this;
    }

    /**
     * Pushes contract in signed contracts array.
     **/
    public pushSignedContract(contract) {
        this.pullRefusedContract(contract);
        this.signedContracts.push(contract);

        this.decideIfContractsHasValidated();
    }

    /**
     * Pulls contract from signed contracts array.
     **/
    public pullRefusedContract(contract) {
        this.signedContracts = this.signedContracts.filter(signed => signed.id != contract.id);
        this.decideIfContractsHasValidated();
    }

    /**
     * Decide if all required acceptance contracts were singed.
     **/
    private decideIfContractsHasValidated() {
        const ready = this.requiredAcceptanceContracts.length === this.signedContracts.length;
        if (ready) {
            this.contractsAcceptedSource.next();
        } else {
            this.contractsArePendingSource.next();
        }
    }

    /**
     * Set billing address as delivery address if billing address is empty.
     **/
    private buildBillingAddress() {
        const hasBillingAddress = this.data.deliveries.billing_address !== undefined;
        const hasDeliveryAddress = this.data.deliveries.delivery_address !== undefined;

        if (!hasBillingAddress && hasDeliveryAddress) {
            this.data.deliveries.billing_address = this.data.deliveries.delivery_address;
        }
    }

    /**
     * Normalize card data if an option was selected or form was filled.
     **/
    private buildCard() {
        if (this.data.card !== undefined) {
            this.data.card = this.prepareSingleCardObject(this.data.card);
        }
        if (null === this.data.card) {
            delete this.data.card;
        }

        if (this.data.cards !== undefined && this.data.cards.length > 0) {
            this.data.cards = this.prepareMultipleCardsObject(this.data.cards);
        } else if (this.data.cards == undefined || this.data.cards.length === 0) {
            delete this.data.cards;
        }
    }

    /**
     * Normalize each single card object.
     **/
    private prepareMultipleCardsObject(cardsArr) {
        const preparedCardsArr = [];
        for (let i = 0; i < cardsArr.length; i++) {
            let card = this.prepareSingleCardObject(cardsArr[i]);
            if (null === card) {
                continue;
            }

            preparedCardsArr.push(card);
        }

        return preparedCardsArr;
    }

    /**
     * Normalize a single card object.
     **/
    private prepareSingleCardObject(card) {
        if (card === undefined) {
            return null;
        }
        if (false === isNaN(Number(card.id))) {
            const cardModel = new CardModel();
            cardModel.id = card.id;
            card = cardModel;
        }
        if (card.expiration_date !== undefined) {
            card.expiration_date = card.expiration_date.split(' / ').join('/');
        }
        if (card.number !== undefined) {
            card.number = card.number.replace(/ /g, '');
        }
        if (card.holder_name !== undefined) {
            card.holder_name = card.holder_name.toUpperCase();
        }
        if (card.must_be_tokenized === undefined) {
            card.must_be_tokenized = false;
        }

        const emptyCard = card.number === undefined && card.id === undefined;
        if (emptyCard) {
            return null;
        }

        return card;
    }

    /**
     * Normalize scheduling data, removing if empty.
     **/
    private buildSchedule() {
        if (this.data.scheduling !== undefined && this.data.scheduling.date === undefined) {
            delete this.data.scheduling;
        }
    }

    /**
     * Check if exists and include reCaptchaToken into payment body.
     **/
    private buildReCaptchaToken() {
        if (null !== this.reCaptchaToken) {
            this.data.recaptcha_token = this.reCaptchaToken;
        }
    }

    /**
     * Get affiliate shareable key from localStorage if has.
     **/
    private buildAffiliateShareableKey() {
        const affiliateShareableKey = localStorage.getItem('affiliate-shareable-key');
        if (affiliateShareableKey) {
            this.data.affiliate_shareable_key = affiliateShareableKey;
        }
    }

    /**
     * Set contracts ids in data payload or remove it.
     **/
    private buildContracts() {
        if (this.requiredAcceptanceContracts.length == 0) {
            delete this.data.contracts;
            return;
        }

        this.data.contracts = this.signedContracts.map(contract => contract.id);
    }

    /**
     * Set PagarMe 3DS data in data payload or remove it.
     **/
    private buildPagarMe3ds() {
        if (this.data.pagar_me_3ds === undefined ||
            (typeof this.data.pagar_me_3ds === 'object' && Object.keys(this.data.pagar_me_3ds).length === 0)) {
            delete this.data.pagar_me_3ds;
        }
    }

    /**
     * Set 3ds data in data payload or remove it.
     **/
    private build3ds() {
        if (this.data.three_ds === undefined ||
            (typeof this.data.three_ds === 'object' && Object.keys(this.data.three_ds).length === 0)) {
            delete this.data.three_ds;
        }
    }

    /**
     * Set fingerprint data in data payload or remove it.
     **/
    private buildFingerprint() {
        if (this.data.fingerprint === undefined) {
            delete this.data.fingerprint;
        }
    }

    /**
     * Set browser data in data payload or remove it.
     **/
    private buildBrowser() {
        if (this.data.browser === undefined ||
            (typeof this.data.browser === 'object' && Object.keys(this.data.browser).length === 0)) {
            delete this.data.browser;
        }
    }

    /**
     * Handle refuse response.
     **/
    private handleRefused(reason: string) {
        this.refusedSource.next(reason);
    }
}



// WEBPACK FOOTER //
// /codebuild/output/src2078029388/src/src/app/payment/services/payment.service.ts